import React from "react";
import { View, StyleSheet } from "react-native";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import featurecodedata from "../data/featurecodedata";
import featurecodecsvcolumndata from "../data/featurecodecsvcolumndata";
import BaseController from "../base/BaseController";
import NtListEmpty from "../../components/NtListEmpty";
import NtListHeader from "../../components/NtListHeader";
import NtListRow from "../../components/NtListRow";
import NtPageSection from "../../components/NtPageSection";
import NtTabController from "../../components/NtTabController";
import NtBadge from "../../components/NtBadge";
import { asyncStorageSetData } from "../utils/ControllerUtils";

const ALL_TAB = { id: "all", title: "All" };

// Tab strip = "All" + each category (in the order it first appears in data).
const tabs = [
  ALL_TAB,
  ...[...new Set(featurecodedata.map((c) => c.category))].map((c) => ({
    id: c,
    title: c,
  })),
];

class FeatureCodeController extends BaseController {
  state = {
    ...super.state,
    searchText: "",
    tab: ALL_TAB,
  };

  column_location = "@featurecode_column_storage";

  componentDidMount() {
    this.handleDefaultColumnsFetch(
      this.column_location,
      featurecodecsvcolumndata.filter((each) => each.default === true),
    );
  }

  handleExportDataParce = (item, column) => {
    if (column === "details") return `"${item[column]}"`;
    return item[column];
  };

  // Filter against the static dataset on every render — small list, cheap.
  getFiltered = () => {
    const text = this.state.searchText?.trim().toLowerCase();
    const tabId = this.state.tab?.id;
    return featurecodedata.filter((each) => {
      if (tabId !== ALL_TAB.id && each.category !== tabId) return false;
      if (!text) return true;
      return (
        each.code?.toLowerCase().includes(text) ||
        each.name?.toLowerCase().includes(text) ||
        each.details?.toLowerCase().includes(text)
      );
    });
  };

  // Group preserving the order categories first appear in the data file.
  getGrouped = (items) => {
    const groups = new Map();
    for (const item of items) {
      if (!groups.has(item.category)) groups.set(item.category, []);
      groups.get(item.category).push(item);
    }
    return [...groups.entries()];
  };

  renderRow = (item, index) => (
    <NtListRow
      key={item.code + index}
      item={item}
      columns={this.state.columns}
      renderCustom={(row, column) => {
        if (column === "code") return <NtBadge variant="gray" dot={false}>{row[column]}</NtBadge>;
      }}
    />
  );

  renderSection = ([category, rows], isFirst) => (
    <NtPageSection
      key={category}
      title={category}
      style={isFirst ? null : { marginTop: 12 }}
    >
      <View style={styles.tableWrap}>
        <NtListHeader
          columns={isFirst ? featurecodecsvcolumndata : null}
          selectedColumns={this.state.columns}
          onColumnUpdate={(updated) => {
            asyncStorageSetData(this.column_location, updated);
            this.setState({ columns: updated });
          }}
          exportFileName={isFirst ? "feature-code" : undefined}
          exportDataParser={this.handleExportDataParce}
          exportFetchDataAsync={isFirst ? async () => featurecodedata : undefined}
        />
        {rows.map((each, index) => this.renderRow(each, index))}
      </View>
    </NtPageSection>
  );

  render() {
    const filtered = this.getFiltered();
    const groups = this.getGrouped(filtered);
    const subtitle = `Star codes available system-wide · ${filtered.length} code${filtered.length === 1 ? "" : "s"}`;

    return (
      <NtRootPageContainer
        pageTitle="Feature Codes"
        pageSubtitle={subtitle}
        permission={true}
        searchText={this.state.searchText}
        setSearchText={(text) => this.setState({ searchText: text })}
      >
        <NtTabController
          containerStyle={styles.tabs}
          options={tabs}
          selection={this.state.tab}
          onPress={(item) => this.setState({ tab: item })}
        />

        {groups.length === 0 ? (
          <NtPageSection>
            <NtListEmpty visible={true} />
          </NtPageSection>
        ) : (
          groups.map((group, i) => this.renderSection(group, i === 0))
        )}
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
  tabs: { marginBottom: 12 },
});

export default FeatureCodeController;
